import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

import type { FormField, FormSchema } from "@/lib/database";
import {
	BasicSettings,
	ConditionalVisibilitySettings,
	EmptyState,
	FieldSpecificSettings,
	OptionsSettings,
	PrepopulationSettings,
	SettingsPanelHeader,
	ValidationSettings,
} from "./components";

import { useFieldUpdates } from "./utils";

interface FieldSettingsPanelProps {
	field: FormField | null;
	onFieldUpdate: (field: FormField) => void;
	onClose: () => void;
	schema?: FormSchema;
}

export function FieldSettingsPanel({
	field,
	onFieldUpdate,
	onClose,
	schema,
}: FieldSettingsPanelProps) {
	const { updateField, updateValidation, updateSettings } = useFieldUpdates(
		field,
		onFieldUpdate
	);

	if (!field) {
		return <EmptyState onClose={onClose} />;
	}

	return (
		<div
			className="flex h-full min-h-0 flex-col border-border bg-background"
			style={{
				overscrollBehavior: "contain",
			}}
		>
			<SettingsPanelHeader onClose={onClose} />
			<ScrollArea
				className="h-0 min-h-0 flex-1"
				style={{
					overscrollBehavior: "contain",
				}}
			>
				<div className="flex flex-col gap-4 p-4">
					{field.type !== "banner" && field.type !== "statement" && (
						<>
							<BasicSettings field={field} onFieldUpdate={onFieldUpdate} />
							<Separator />
						</>
					)}
					<FieldSpecificSettings
						field={field}
						onFieldUpdate={onFieldUpdate}
						onUpdateSettings={updateSettings}
						schema={schema}
					/>
					{["select", "radio", "checkbox"].includes(field.type) && (
						<>
							<Separator />
							<OptionsSettings field={field} onFieldUpdate={onFieldUpdate} />
						</>
					)}
					<Separator />
					<PrepopulationSettings field={field} onFieldUpdate={onFieldUpdate} />
					{!["file", "signature", "statement", "banner", "field-group"].includes(
						field.type
					) && (
						<>
							<Separator />
							<ConditionalVisibilitySettings
								field={field}
								onUpdateSettings={updateSettings}
							/>
						</>
					)}
					<Separator />
					<ValidationSettings
						field={field}
						onUpdateValidation={updateValidation}
					/>
				</div>
			</ScrollArea>
		</div>
	);
}
